import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import Loader from "../../../../components/Common/Loader";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  HOTELADMIN,
  HOTELADMINADD,
  HOTELADMINUPDATE,
  SINGLEHOTELADMIN,
} from "../../../../services/ApiCalls";
import GoogleAutocomplete from "../../../../components/Common/GoogleAutoComplete";
import { Country, State, City } from "country-state-city";
import fileUploader from "../../../../utilities/fileUploader";
import Loading from "../../../../components/Common/Loading";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const getSchema = (t) => z.object({
  name: z.string().min(1, { message: t("manageHotelAdmin.form.validation.nameRequired") }),
  address: z.string().min(1, { message: t("manageHotelAdmin.form.validation.addressRequired") }),
  mobileNumber: z
    .string()
    .min(7, { message: t("manageHotelAdmin.form.validation.mobileNumberRequired") })
    .regex(/^[0-9]+$/, { message: t("manageHotelAdmin.form.validation.mobileNumberOnlyNumbers") }),
  description: z.string().min(1, { message: t("manageHotelAdmin.form.validation.descriptionRequired") }),
  profileImage: z.string().nonempty({ message: t("manageHotelAdmin.form.validation.profileImageRequired") }),
  password: z.string().optional(),
  email: z.string().email({ message: t("manageHotelAdmin.form.validation.emailInvalid") }),
  status: z.boolean(),
  country: z.string().min(1, { message: t("manageHotelAdmin.form.validation.countryRequired") }),
  state: z.string().min(1, { message: t("manageHotelAdmin.form.validation.stateRequired") }),
  city: z.string().min(1, { message: t("manageHotelAdmin.form.validation.cityRequired") }),
  countryCode: z.string().optional(),
  lat: z.any().optional(),
  lng: z.any().optional(),
});

const AddEditHotelAdmin = () => {
  const { t } = useTranslation();
  const schema = getSchema(t);
  const [loader, setLoader] = useState(false);
  const [hotelData, setHotelData] = useState();
  const { id } = useParams();
  const navigate = useNavigate();
  const [imgloader, setImgLoader] = useState(false);
  const [imagePreview, setImagePreview] = useState();
  const [pass, setPass] = useState(false);
  const location = useLocation();
  const handlePass = () => {
    setPass(!pass);
  };

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      country: "",
      state: "",
      profileImage: "",
      city: "",
      address: "",
      mobileNumber: "",
      password: "",
      description: "",
      email: "",
      status: false,
      countryCode: "",
      lat: "",
      lng: "",
    },
  });

  const submitHandler = catchAsync(async (data) => {
    if (!id && !data.password) {
      // on add, password must be provided
      return toast.error(t("manageHotelAdmin.form.validation.passwordRequired"));
    }
    if (id && !data.password?.trim()) {
      delete data.password;
    }
    const body = {
      ...data,
      status: data.status ? "Active" : "Inactive",
      location: {
        type: "Point",
        coordinates: [Number(watch("lng")), Number(watch("lat"))],
      },
      ...(id && { _id: id }),
    };

    console.log(body, "body ===== have ====<>");

    const res = await (id ? HOTELADMINUPDATE(body) : HOTELADMINADD(body));
    const success = checkResponse({ res, showSuccess: true });
    if (success) navigate(`/manage-hotel-admin?page=${page}`);
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await SINGLEHOTELADMIN({ id: id });
    const success = checkResponse({ res, setData: setHotelData, setLoader });

    if (success && res?.data?.data) {
      const data = res.data.data;
      reset({
        name: data.name || "",
        address: data.address || "",
        mobileNumber: data.mobileNumber || "",
        description: data.description || "",
        email: data.email || "",
        password: data?.password || "",
        profileImage: data?.profileImage || "",
        status: data.status === "active",
        country: data.country || "",
        state: data.state || "",
        city: data.city || "",
        countryCode: data.countryCode || "",
        lat: data.lat || "",
        lng: data.lng || "",
      });
    }
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const addressChangeHandler = (address) => {
    setValue("address", address?.address, { shouldValidate: true });
    setValue("lat", address?.lat, { shouldValidate: true });
    setValue("lng", address?.lng, { shouldValidate: true });
  };

  const countryOptions = Country.getAllCountries().filter((c) => ["Thailand", "Vietnam"].includes(c.name)).map((c) => ({
    value: c.isoCode,
    label: c.name,
  }));

  const stateOptions = (countryCode) =>
    State.getStatesOfCountry(countryCode).map((s) => ({
      value: s.isoCode,
      label: s.name,
    }));

  const cityOptions = (countryCode, stateCode) =>
    City.getCitiesOfState(countryCode, stateCode).map((ci) => ({
      value: ci.name,
      label: ci.name,
    }));

  const handleImageChange = catchAsync(async (e) => {
    setImgLoader(true);
    const file = e.target.files[0];
    const res = await fileUploader(file);

    setValue("profileImage", res?.url);
    setImgLoader(false);

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  });

  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row className="justify-content-center">
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/manage-hotel-admin?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id
                    ? t("manageHotelAdmin.form.edit")
                    : t("manageHotelAdmin.form.add")}
                </h4>
              </div>
            </Col>
            <Col lg="12">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row>
                    <Col lg="6" className="my-2">
                      <label>{t("manageHotelAdmin.columns.name")}</label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("name")}
                      />
                      {errors?.name && (
                        <p className="text-danger">{errors.name.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotelAdmin.columns.email")}</label>
                      <input
                        type="email"
                        className="form-control"
                        {...register("email")}
                      />
                      {errors?.email && (
                        <p className="text-danger">{errors.email.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageHotelAdmin.columns.mobileNumber")}
                      </label>
                      <Controller
                        control={control}
                        name="mobileNumber"
                        render={({ field }) => (
                          <PhoneInput
                            {...field}
                            country={"us"}
                            value={field.value}
                            onChange={(value, { dialCode }) => {
                              setValue("countryCode", dialCode);
                              field.onChange(value);
                            }}
                          />
                        )}
                      />
                      {errors?.mobileNumber && (
                        <p className="text-danger">
                          {errors.mobileNumber.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotelAdmin.columns.address")}</label>
                      <GoogleAutocomplete
                        lat={watch("lat")}
                        lng={watch("lng")}
                        name={"address"}
                        address={watch("address")}
                        onChange={addressChangeHandler}
                      />
                      {errors?.address && (
                        <p className="text-danger">
                          {errors?.address?.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotelAdmin.columns.country")}</label>
                      <Controller
                        control={control}
                        name="country"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={countryOptions}
                            value={countryOptions.find(
                              (c) =>
                                c.label === field.value ||
                                c.value === field.value
                            )}
                            onChange={(option) => {
                              setValue("country", option.label, {
                                shouldValidate: true,
                              });
                              setValue("state", "");
                              setValue("city", "");
                            }}
                          />
                        )}
                      />
                      {errors?.country && (
                        <p className="text-danger">{errors.country.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotelAdmin.columns.state")}</label>
                      <Controller
                        control={control}
                        name="state"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={stateOptions(
                              Country.getAllCountries().find(
                                (c) => c.name === watch("country")
                              )?.isoCode
                            )}
                            value={stateOptions(
                              Country.getAllCountries().find(
                                (c) => c.name === watch("country")
                              )?.isoCode
                            ).find((s) => s.label === field.value)}
                            onChange={(option) => {
                              setValue("state", option.label, {
                                shouldValidate: true,
                              });
                              setValue("city", "");
                            }}
                          />
                        )}
                      />
                      {errors?.state && (
                        <p className="text-danger">{errors.state.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotelAdmin.columns.city")}</label>
                      <Controller
                        control={control}
                        name="city"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={cityOptions(
                              Country.getAllCountries().find(
                                (c) => c.name === watch("country")
                              )?.isoCode,
                              State.getStatesOfCountry(
                                Country.getAllCountries().find(
                                  (c) => c.name === watch("country")
                                )?.isoCode
                              ).find((s) => s.name === watch("state"))?.isoCode
                            )}
                            value={cityOptions(
                              Country.getAllCountries().find(
                                (c) => c.name === watch("country")
                              )?.isoCode,
                              State.getStatesOfCountry(
                                Country.getAllCountries().find(
                                  (c) => c.name === watch("country")
                                )?.isoCode
                              ).find((s) => s.name === watch("state"))?.isoCode
                            ).find((ci) => ci.label === field.value)}
                            onChange={(option) =>
                              setValue("city", option.label, {
                                shouldValidate: true,
                              })
                            }
                          />
                        )}
                      />
                      {errors?.city && (
                        <p className="text-danger">{errors.city.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotelAdmin.form.password")}</label>
                      <div className="position-relative iconWithText">
                        <input
                          type={pass ? "text" : "password"}
                          className="form-control"
                          {...register("password")}
                        />
                        {errors?.password && (
                          <p className="text-danger">
                            {errors.password.message}
                          </p>
                        )}
                        <Button
                          onClick={handlePass}
                          className="border-0 p-0 position-absolute icn"
                          variant="transparent"
                          style={{ right: 10 }}
                        >
                          {pass ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 14 11"
                              fill="none"
                            >
                              <path
                                d="M8.96236 5.23432C8.96236 5.60618 8.8521 5.96968 8.64551 6.27886C8.43892 6.58804 8.14529 6.82902 7.80174 6.97132C7.4582 7.11362 7.08017 7.15085 6.71546 7.07831C6.35076 7.00576 6.01576 6.8267 5.75282 6.56376C5.48988 6.30082 5.31082 5.96582 5.23827 5.60112C5.16573 5.23641 5.20296 4.85838 5.34526 4.51484C5.48756 4.17129 5.72854 3.87766 6.03772 3.67107C6.34691 3.46448 6.71041 3.35422 7.08226 3.35422C7.58089 3.35422 8.0591 3.5523 8.41169 3.90489C8.76428 4.25748 8.96236 4.73569 8.96236 5.23432ZM13.8945 5.54768C13.7817 5.73569 11.1621 10.2479 7.08226 10.2479C3.00242 10.2479 0.382804 5.73569 0.269997 5.54768C0.214993 5.45241 0.186035 5.34433 0.186035 5.23432C0.186035 5.12432 0.214993 5.01624 0.269997 4.92097C0.382804 4.73296 3.00242 0.220703 7.08226 0.220703C11.1621 0.220703 13.7817 4.73296 13.8945 4.92097C13.9495 5.01624 13.9785 5.12432 13.9785 5.23432C13.9785 5.34433 13.9495 5.45241 13.8945 5.54768ZM10.2158 5.23432C10.2158 4.61457 10.032 4.00874 9.68768 3.49344C9.34336 2.97813 8.85397 2.5765 8.2814 2.33934C7.70882 2.10217 7.07878 2.04011 6.47094 2.16102C5.8631 2.28193 5.30476 2.58037 4.86653 3.0186C4.4283 3.45683 4.12986 4.01516 4.00895 4.62301C3.88804 5.23085 3.9501 5.86089 4.18727 6.43347C4.42443 7.00604 4.82607 7.49543 5.34137 7.83975C5.85667 8.18406 6.46251 8.36784 7.08226 8.36784C7.91331 8.36784 8.71034 8.0377 9.29798 7.45005C9.88563 6.86241 10.2158 6.06538 10.2158 5.23432Z"
                                fill="#B1B1B1"
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 14 11"
                              fill="none"
                            >
                              <path
                                d="M8.96236 5.23432C8.96236 5.60618 8.8521 5.96968 8.64551 6.27886C8.43892 6.58804 8.14529 6.82902 7.80174 6.97132C7.4582 7.11362 7.08017 7.15085 6.71546 7.07831C6.35076 7.00576 6.01576 6.8267 5.75282 6.56376C5.48988 6.30082 5.31082 5.96582 5.23827 5.60112C5.16573 5.23641 5.20296 4.85838 5.34526 4.51484C5.48756 4.17129 5.72854 3.87766 6.03772 3.67107C6.34691 3.46448 6.71041 3.35422 7.08226 3.35422C7.58089 3.35422 8.0591 3.5523 8.41169 3.90489C8.76428 4.25748 8.96236 4.73569 8.96236 5.23432ZM13.8945 5.54768C13.7817 5.73569 11.1621 10.2479 7.08226 10.2479C3.00242 10.2479 0.382804 5.73569 0.269997 5.54768C0.214993 5.45241 0.186035 5.34433 0.186035 5.23432C0.186035 5.12432 0.214993 5.01624 0.269997 4.92097C0.382804 4.73296 3.00242 0.220703 7.08226 0.220703C11.1621 0.220703 13.7817 4.73296 13.8945 4.92097C13.9495 5.01624 13.9785 5.12432 13.9785 5.23432C13.9785 5.34433 13.9495 5.45241 13.8945 5.54768ZM10.2158 5.23432C10.2158 4.61457 10.032 4.00874 9.68768 3.49344C9.34336 2.97813 8.85397 2.5765 8.2814 2.33934C7.70882 2.10217 7.07878 2.04011 6.47094 2.16102C5.8631 2.28193 5.30476 2.58037 4.86653 3.0186C4.4283 3.45683 4.12986 4.01516 4.00895 4.62301C3.88804 5.23085 3.9501 5.86089 4.18727 6.43347C4.42443 7.00604 4.82607 7.49543 5.34137 7.83975C5.85667 8.18406 6.46251 8.36784 7.08226 8.36784C7.91331 8.36784 8.71034 8.0377 9.29798 7.45005C9.88563 6.86241 10.2158 6.06538 10.2158 5.23432Z"
                                fill="#B1B1B1"
                              />
                              <line
                                x1="1"
                                y1="1"
                                x2="13"
                                y2="10"
                                stroke="#B1B1B1"
                                strokeWidth="1.5"
                              />
                            </svg>
                          )}
                        </Button>
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <label>{t("manageHotelAdmin.form.description")}</label>
                      <textarea
                        className="form-control"
                        {...register("description")}
                        rows={3}
                      ></textarea>
                      {errors?.description && (
                        <p className="text-danger">{errors.description.message}</p>
                      )}
                    </Col>

                    <Col lg="5" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          {t("manageHotelAdmin.form.image")}
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleImageChange}
                        />
                        {errors?.profileImage && (
                          <p className="text-danger m-0">
                            {errors?.profileImage?.message}
                          </p>
                        )}
                        {imgloader ? (
                          <div
                            style={{ height: 140, width: 140 }}
                            className="img-fluid rounded-circle object-fit-cover"
                          >
                            <Loading fullSize={true} />
                          </div>
                        ) : (
                          (imagePreview || watch("profileImage")) && (
                            <img
                              src={imagePreview || watch("profileImage")}
                              alt="Preview"
                              style={{ width: "50%", marginTop: "10px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>

                    <Col lg="12" className="my-3">
                      <div className="d-flex align-items-center gap-3 px-4">
                        <Form.Check
                          type="switch"
                          id="status-switch"
                          label={t("manageHotelAdmin.columns.status")}
                          {...register("status")}
                          style={{ transform: "scale(1.4)", cursor: "pointer" }}
                          className="form-switch-success"
                        />
                      </div>
                    </Col>

                    <Col
                      lg="12"
                      className="my-3 d-flex justify-content-between"
                    >
                      <Link to={`/manage-hotel-admin?page=${page}`}>
                        <Button variant="secondary">{t("common.back")}</Button>
                      </Link>
                      <Button type="submit" variant="primary">
                        {id ? t("common.update") : t("common.submit")}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddEditHotelAdmin;
